import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { ECampaignStatus, EPlatformFeature } from '@enums/campaign.enum';
import {
  ICampaignAnalyticsApi,
  ICampaignAnalyticsApiResponse,
  ICreatorAnalyticsApi,
  ICreatorAnalyticsSliceApi,
  IPlannedCtaApi,
} from '@interfaces/campaign-analytics-api.interface';
import { IAnalyticsNode, ICampaignAnalytics, ICreatorAnalytics } from '@interfaces/campaign-analytics.interface';
import { ComponentStore } from '@ngrx/component-store';
import { ICampaignCreator, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CampaignsService } from '@pages/campaigns/campaigns.service';
import { ICreator } from '@pages/creators/creators.interface';
import * as Sentry from '@sentry/angular';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { API_DATE_TIME_FORMAT_WITH_MS, DateUtilitiesService } from '@utilities/date-utilities.service';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

export interface ICreatorDetailsDeliverablesFilter {
  /** Empty set = every campaign the creator is in (no narrowing). */
  campaignIds: Set<string>;
  endDate: Date | null;
  /** Empty set = every platform feature. */
  platformFeatures: Set<EPlatformFeature>;
  startDate: Date | null;
}

export interface ICreatorDetailsDeliverablesFetchParams {
  adminMode: boolean;
  /** The creator's full campaign list (this store's own campaigns$/fetchCreatorCampaigns). */
  campaigns: TCampaignApi[];
  creatorId: string;
  /**
   * The resolved creator - supplies the name/icon the analytics shell assembly needs (see
   * withSyntheticCampaignCreator) and the name used in the Sentry report below.
   */
  creator: ICreator;
  filter: ICreatorDetailsDeliverablesFilter;
}

export interface ICreatorDetailsDeliverablesState {
  analyticsNodes: IAnalyticsNode[] | null;
  /**
   * This creator's full campaign list, fetched independently of the (shared, paginated)
   * CampaignsStore instance the Campaigns tab's datatable also injects and repeatedly overwrites
   * via its own fetchCampaigns() calls (pagination/sort/filter changes) - sharing that instance
   * made this tab's campaign scope-filter/name/icon lookups unstable, resetting whenever the
   * Campaigns tab table changed page. Fetched without joins - only id/name/iconId/status/start/
   * end are read from it here, and the single campaign_creators entry the analytics shell
   * assembly needs is synthesized from the resolved creator input instead (see
   * withSyntheticCampaignCreator), so the list isn't inflated by every other creator of each
   * campaign.
   */
  campaigns: TCampaignApi[];
  campaignsError: string | null;
  campaignsLoading: boolean;
  /**
   * Per-campaign shells, keyed by campaignId - what cta-analytics-dialog needs (resolved via a
   * clicked row's/node's own campaignId), never the merged scopeCampaignAnalytics below. See
   * plan §4.5 ("two different shells, two different jobs").
   */
  campaignAnalyticsById: Record<string, ICampaignAnalytics | undefined>;
  campaignIconById: Record<string, string | null | undefined>;
  campaignNameById: Record<string, string>;
  error: string | null;
  loading: boolean;
  /**
   * Synthetic shell spanning every campaign currently in scope (min/max start-end, one creator
   * entry with plannedCtas unioned across those campaigns) - feeds ONLY
   * ProfileDateFilterComponent/ProfilePlatformFeatureSelectComponent's `campaignAnalytics` input,
   * unchanged. Never passed to the dialog.
   */
  scopeCampaignAnalytics: ICampaignAnalytics | null;
}

const initialState: ICreatorDetailsDeliverablesState = {
  analyticsNodes: null,
  campaignAnalyticsById: {},
  campaignIconById: {},
  campaignNameById: {},
  campaigns: [],
  campaignsError: null,
  campaignsLoading: false,
  error: null,
  loading: false,
  scopeCampaignAnalytics: null,
};

interface IAssembledResult {
  analyticsNodes: IAnalyticsNode[];
  campaignAnalyticsById: Record<string, ICampaignAnalytics | undefined>;
  campaignIconById: Record<string, string | null | undefined>;
  campaignNameById: Record<string, string>;
  scopeCampaignAnalytics: ICampaignAnalytics | null;
}

/**
 * Everything derived from the shells fetch (bulkCampaignAnalytics + creatorAnalyticsShells) -
 * i.e. every campaign's ICampaignAnalytics shell/plannedCtas plus the merged scope shell. Cached
 * across scope-filter changes that only touch date/platform (see cachedShellsScopeKey below) so
 * those never need a fresh shells fetch, only a fresh slices fetch layered on top of this.
 */
interface IShellsData {
  campaignAnalyticsById: Record<string, ICampaignAnalytics | undefined>;
  campaignIconById: Record<string, string | null | undefined>;
  campaignNameById: Record<string, string>;
  scopeCampaignAnalytics: ICampaignAnalytics | null;
}

interface IScopeMergeState {
  creatorId: string | null;
  maxEnd: Date | null;
  mergedPlannedCtas: Map<string, IPlannedCtaApi>;
  minStart: Date | null;
  templateShell: ICampaignAnalytics | null;
}

const EMPTY_RESULT: IAssembledResult = {
  analyticsNodes: [],
  campaignAnalyticsById: {},
  campaignIconById: {},
  campaignNameById: {},
  scopeCampaignAnalytics: null,
};

const CAMPAIGN_ID_BATCH_SIZE = 20;
const SLICES_LIMIT = 1000;

@Injectable()
export class CreatorDetailsDeliverablesStore extends ComponentStore<ICreatorDetailsDeliverablesState> {
  public readonly analyticsNodes$ = this.selectSignal((state) => state.analyticsNodes);
  public readonly campaignAnalyticsById$ = this.selectSignal((state) => state.campaignAnalyticsById);
  public readonly campaignIconById$ = this.selectSignal((state) => state.campaignIconById);
  public readonly campaignNameById$ = this.selectSignal((state) => state.campaignNameById);
  public readonly campaigns$ = this.selectSignal((state) => state.campaigns);
  public readonly campaignsError$ = this.selectSignal((state) => state.campaignsError);
  public readonly campaignsLoading$ = this.selectSignal((state) => state.campaignsLoading);
  public readonly error$ = this.selectSignal((state) => state.error);
  public readonly loading$ = this.selectSignal((state) => state.loading);
  public readonly scopeCampaignAnalytics$ = this.selectSignal((state) => state.scopeCampaignAnalytics);

  // Memoizes the shells fetch (bulkCampaignAnalytics + creatorAnalyticsShells, and everything
  // derived from them: campaignAnalyticsById/plannedCtas/scopeCampaignAnalytics) across scope
  // changes that only touch date/platform. campaign-details.store.ts's own fetchDeliverablesTab
  // fetches its campaignAnalyticsShell ONCE (via fetchCampaignAnalytics) and only ever refetches
  // slices when its own date/platform/creator filters change, reusing that one shell every time.
  // This store used to refetch the shells (and therefore plannedCtas, which is what drives the
  // missing-vs-planned placeholder classification in calculatePlannedCtaAnalyticsNodes) on EVERY
  // single scope filter change, including ones that only touched date/platform, not the campaign
  // selection - identical requests a few seconds apart, whose responses aren't guaranteed to be
  // byte-identical (e.g. server-side analytics reprocessing landing between the two calls), could
  // therefore flip an already-correctly-classified "missing" placeholder to "planned" for no
  // reason visible on this page. Caching the shells and only refetching them when the actual
  // scope (creator/adminMode/selected campaign ids) changes closes that gap and matches
  // campaign-details' own architecture.
  private cachedShellsScopeKey: string | null = null;
  private cachedShellsData: IShellsData | null = null;

  constructor(
    private readonly analyticsService: CampaignAnalyticsService,
    private readonly campaignAnalyticsUtilities: CampaignAnalyticsUtilitiesService,
    private readonly campaignsService: CampaignsService,
    private readonly dateUtilities: DateUtilitiesService,
  ) {
    super(initialState);
  }

  /**
   * Fetches this creator's Live/Ended campaign list on its own, decoupled from CampaignsStore (see
   * `campaigns` on the state interface above for why). No `joins` - id/name/iconId/status/start/
   * end are plain columns, and the campaignCreators entry shell assembly needs is synthesized
   * from the resolved creator instead (see withSyntheticCampaignCreator), so the response isn't
   * inflated by every other creator of each campaign. start/end are parsed the same way
   * CampaignsStore.handleCampaignsResponse() does, and the raw strings are kept in
   * originalStart/originalEnd (same convention as CampaignDetailsStore.setFormattedCampaign)
   * because the analytics shell assembly and the dialog derive campaign bounds by parsing those,
   * so a campaign object handed to CtaAnalyticsDialogComponent is shaped identically regardless
   * of which tab opened the dialog.
   */
  public readonly fetchCreatorCampaigns = this.effect<{ adminMode: boolean; creatorId: string }>((params$) =>
    params$.pipe(
      tap(() => this.patchState({ campaignsError: null, campaignsLoading: true })),
      switchMap(({ adminMode, creatorId }) => {
        const httpParams = new HttpParams()
          .set('campaign_creators__creator_id', creatorId)
          .set('status__in', [ECampaignStatus.live, ECampaignStatus.ended].join(','))
          .set('limit', '1000');
        const fetch$ = adminMode
          ? this.campaignsService.getAdminCampaigns(httpParams)
          : this.campaignsService.getCampaigns(httpParams);

        return fetch$.pipe(
          tap((response: IApiResponse<TCampaignApi[]>) => {
            const campaigns: TCampaignApi[] = (response.data ?? []).map((campaign) => ({
              ...campaign,
              end: this.dateUtilities.parseDateTimeFromApi(campaign.end as string),
              originalEnd: campaign.end as string,
              originalStart: campaign.start as string,
              start: this.dateUtilities.parseDateTimeFromApi(campaign.start as string),
            }));

            this.patchState({ campaigns, campaignsLoading: false });
          }),
          // Keep whatever campaign list is already cached rather than wiping this tab's campaign
          // filter/name/icon lookups out from under an otherwise-working page - the campaignsError
          // signal lets the tab surface the failure and offer a retry instead of silently showing
          // the empty/no-campaigns state.
          catchError((error: HttpErrorResponse) => {
            this.patchState({
              campaignsError: `Failed to load campaigns: ${error.status}`,
              campaignsLoading: false,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  /**
   * Triggers a (re)fetch for `params.creatorId`, scoped to every campaign in
   * `params.filter.campaignIds` (or every campaign the creator is in, when that set is empty).
   * Call this whenever the creator, campaign list, or any Scope filter changes - switchMap
   * cancels a still-in-flight previous call, so a rapid string of filter changes can never let a
   * stale response land after a newer one already resolved. Internally reuses the last-fetched
   * shells (see cachedShellsScopeKey) whenever only date/platform changed, refetching only slices.
   */
  public readonly fetchDeliverables = this.effect<ICreatorDetailsDeliverablesFetchParams>((params$) =>
    params$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((params) =>
        this.buildFetchRequest(params).pipe(
          tap((result) => {
            this.patchState({
              analyticsNodes: result.analyticsNodes,
              campaignAnalyticsById: result.campaignAnalyticsById,
              campaignIconById: result.campaignIconById,
              campaignNameById: result.campaignNameById,
              loading: false,
              scopeCampaignAnalytics: result.scopeCampaignAnalytics,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.patchState({
              analyticsNodes: [],
              error: `Failed to load deliverables: ${error.status}`,
              loading: false,
            });

            return EMPTY;
          }),
        ),
      ),
    ),
  );

  private buildFetchRequest(params: ICreatorDetailsDeliverablesFetchParams): Observable<IAssembledResult> {
    const { adminMode, campaigns, creatorId, creator, filter } = params;
    const selectedCampaignIds = this.resolveSelectedCampaignIds(campaigns, filter);

    if (!selectedCampaignIds.length) {
      this.cachedShellsScopeKey = null;
      this.cachedShellsData = null;

      return of(EMPTY_RESULT);
    }

    const scopedCampaigns = campaigns
      .filter((campaign) => selectedCampaignIds.includes(campaign.id))
      .map((campaign) => this.withSyntheticCampaignCreator(campaign, creator));
    const scopeKey = this.buildShellsScopeKey(adminMode, creatorId, selectedCampaignIds);

    if (this.cachedShellsScopeKey === scopeKey && this.cachedShellsData) {
      return this.fetchSlicesAndAssemble({
        adminMode,
        creator,
        creatorId,
        filter,
        scopedCampaigns,
        selectedCampaignIds,
        shellsData: this.cachedShellsData,
      });
    }

    return this.fetchShellsData(adminMode, creatorId, scopedCampaigns, selectedCampaignIds).pipe(
      switchMap((shellsData) => {
        this.cachedShellsScopeKey = scopeKey;
        this.cachedShellsData = shellsData;

        return this.fetchSlicesAndAssemble({
          adminMode,
          creator,
          creatorId,
          filter,
          scopedCampaigns,
          selectedCampaignIds,
          shellsData,
        });
      }),
    );
  }

  private resolveSelectedCampaignIds(campaigns: TCampaignApi[], filter: ICreatorDetailsDeliverablesFilter): string[] {
    return filter.campaignIds.size > 0
      ? campaigns.map((campaign) => campaign.id).filter((id) => filter.campaignIds.has(id))
      : campaigns.map((campaign) => campaign.id);
  }

  // Only the creator/adminMode/campaign-selection identify "which shells are needed" - date and
  // platform filters never affect which shells to fetch, only which slices, so they're
  // deliberately excluded from this key.
  private buildShellsScopeKey(adminMode: boolean, creatorId: string, selectedCampaignIds: string[]): string {
    return `${adminMode}::${creatorId}::${[...selectedCampaignIds].sort().join(',')}`;
  }

  private fetchShellsData(
    adminMode: boolean,
    creatorId: string,
    scopedCampaigns: TCampaignApi[],
    selectedCampaignIds: string[],
  ): Observable<IShellsData> {
    const campaignIdBatches = this.chunkCampaignIds(selectedCampaignIds);

    const bulkCampaignAnalytics$ = forkJoin(
      campaignIdBatches.map((campaignIds) =>
        adminMode
          ? this.analyticsService.getBulkCampaignAnalyticsAdmin(campaignIds)
          : this.analyticsService.getBulkCampaignAnalytics(campaignIds),
      ),
    ).pipe(map((responses) => responses.flat()));

    const creatorAnalyticsShells$ = forkJoin(
      campaignIdBatches.map((campaignIds) => {
        const shellsParams = new HttpParams()
          .set('creator_id', creatorId)
          .set('campaign_id__in', campaignIds.join(','))
          .set('limit', String(Math.max(100, campaignIds.length)));

        return adminMode
          ? this.analyticsService.getCreatorAnalyticsShellsAdmin(shellsParams)
          : this.analyticsService.getCreatorAnalyticsShells(shellsParams);
      }),
    ).pipe(map((responses) => responses.flatMap((response) => response.data ?? [])));

    return forkJoin([bulkCampaignAnalytics$, creatorAnalyticsShells$]).pipe(
      map(([bulkResponses, shells]) => this.assembleShellsData(scopedCampaigns, bulkResponses, shells)),
    );
  }

  private chunkCampaignIds(campaignIds: string[]): string[][] {
    const batches: string[][] = [];

    for (let index = 0; index < campaignIds.length; index += CAMPAIGN_ID_BATCH_SIZE) {
      batches.push(campaignIds.slice(index, index + CAMPAIGN_ID_BATCH_SIZE));
    }

    return batches;
  }

  /**
   * Builds, per campaign, the same ICampaignAnalytics shell campaign-details itself would
   * compute (via the unmodified getCampaignAnalyticsData utility), by patching the campaign-level
   * bulk-fetch response's `creatorAnalytics` array down to just this one creator's shell entry
   * for that campaign - the only thing that differs from what campaign-details does for its own
   * single-campaign case. Also builds the merged, cross-campaign "scope shell" that feeds the
   * Scope section's platform filter (see plan §4.2): rather than hand-constructing a synthetic
   * ICampaignAnalytics/ICreatorAnalytics from scratch (many required fields with no real bearing
   * on what that filter component actually reads), it clones one already-correct per-campaign
   * shell/creator-entry and overrides only start/end/creators - guaranteed type-valid, and every
   * field the filter component doesn't read keeps a real, harmless value instead of an invented
   * placeholder.
   */
  private assembleShellsData(
    campaigns: TCampaignApi[],
    bulkResponses: ICampaignAnalyticsApiResponse[],
    shells: ICreatorAnalyticsApi[],
  ): IShellsData {
    const bulkByCampaignId = new Map(
      bulkResponses.map((response) => [response.campaignAnalytics.id, response.campaignAnalytics]),
    );
    const shellByCampaignId = new Map(shells.map((shell) => [shell.campaignId, shell]));

    const campaignAnalyticsById: Record<string, ICampaignAnalytics | undefined> = {};
    const campaignIconById: Record<string, string | null | undefined> = {};
    const campaignNameById: Record<string, string> = {};
    const mergeState: IScopeMergeState = {
      creatorId: null,
      maxEnd: null,
      mergedPlannedCtas: new Map(),
      minStart: null,
      templateShell: null,
    };

    for (const campaign of campaigns) {
      campaignNameById[campaign.id] = campaign.name;
      campaignIconById[campaign.id] = campaign.iconId;

      const baseApi = bulkByCampaignId.get(campaign.id);

      if (!baseApi) {
        continue;
      }

      const shellApi = shellByCampaignId.get(campaign.id);
      const shell = this.buildCampaignShell(campaign, baseApi, shellApi);

      campaignAnalyticsById[campaign.id] = shell;

      this.mergeIntoScopeState(campaign, shell, mergeState);
    }

    return {
      campaignAnalyticsById,
      campaignIconById,
      campaignNameById,
      scopeCampaignAnalytics: this.buildScopeCampaignAnalytics(mergeState),
    };
  }

  // Refetches only the slices (the one part of the request that genuinely depends on
  // date/platform), then recomputes each in-scope campaign's analyticsNodes from its CACHED
  // shell (never refetched here) plus the freshly-fetched slices for that campaign.
  private fetchSlicesAndAssemble(params: {
    adminMode: boolean;
    creatorId: string;
    creator: ICreator;
    filter: ICreatorDetailsDeliverablesFilter;
    scopedCampaigns: TCampaignApi[];
    selectedCampaignIds: string[];
    shellsData: IShellsData;
  }): Observable<IAssembledResult> {
    const { adminMode, creatorId, creator, filter, scopedCampaigns, selectedCampaignIds, shellsData } = params;
    const campaignIdBatches = this.chunkCampaignIds(selectedCampaignIds);
    const creatorAnalyticsSlices$ = forkJoin(
      campaignIdBatches.map((campaignIds) => {
        const slicesParams = this.buildSlicesParams(creatorId, campaignIds, filter);

        return adminMode
          ? this.analyticsService.getCreatorAnalyticsSlicesAdmin(slicesParams)
          : this.analyticsService.getCreatorAnalyticsSlices(slicesParams);
      }),
    );

    return creatorAnalyticsSlices$.pipe(
      map((slicesResponses: IApiResponse<ICreatorAnalyticsSliceApi[]>[]) => {
        const combinedSlices = slicesResponses.flatMap((response) => response.data ?? []);
        const total = slicesResponses.reduce((sum, response) => sum + (response.meta?.total ?? 0), 0);
        const slices = combinedSlices.slice(0, SLICES_LIMIT);

        this.reportIfSlicesLimitExceeded(total, combinedSlices.length, slices.length, {
          creatorId,
          creatorName: creator.name,
          selectedCampaignIds,
        });

        const slicesByCampaignId = this.groupSlicesByCampaignId(slices);
        let analyticsNodes: IAnalyticsNode[] = [];

        for (const campaign of scopedCampaigns) {
          const shell = shellsData.campaignAnalyticsById[campaign.id];

          if (!shell) {
            continue;
          }

          const campaignSlices = slicesByCampaignId.get(campaign.id) ?? [];

          analyticsNodes = analyticsNodes.concat(
            this.campaignAnalyticsUtilities.calculateAnalyticsNodes(shell, campaignSlices),
          );
        }

        return {
          analyticsNodes,
          campaignAnalyticsById: shellsData.campaignAnalyticsById,
          campaignIconById: shellsData.campaignIconById,
          campaignNameById: shellsData.campaignNameById,
          scopeCampaignAnalytics: shellsData.scopeCampaignAnalytics,
        };
      }),
    );
  }

  // buildSlicesParams caps every request at SLICES_LIMIT rows (`limit: '1000'`) - a deliberate,
  // accepted limit on how much of the table is ever populated; campaign ids are split into
  // batches (see chunkCampaignIds) only to keep each GET URL within proxy limits, so each batch
  // still asks for up to SLICES_LIMIT rows. The combined result is capped back to SLICES_LIMIT
  // here so the table never renders more rows than a single old-style request would have. Report
  // to Sentry whenever either cap actually truncated something (a server page with more rows than
  // it returned, or the combined result exceeding SLICES_LIMIT) rather than letting a creator
  // notice missing rows. Fingerprinted to one issue regardless of which creator/campaigns
  // triggered it - the message still names the specific creator for whoever triages the alert,
  // but the details that make it unique to a particular creator (id/name/campaign ids) belong in
  // `extra`, not the fingerprint.
  private reportIfSlicesLimitExceeded(
    total: number,
    fetchedCount: number,
    renderedCount: number,
    context: { creatorId: string; creatorName: string; selectedCampaignIds: string[] },
  ): void {
    if (total <= fetchedCount && fetchedCount <= renderedCount) {
      return;
    }

    Sentry.withScope((scope) => {
      scope.setFingerprint(['creator-details-deliverables-slices-limit-exceeded']);
      scope.setTag('creatorId', context.creatorId);
      scope.setExtra('campaignIds', context.selectedCampaignIds);
      scope.setExtra('fetchedCount', fetchedCount);
      scope.setExtra('renderedCount', renderedCount);
      scope.setExtra('total', total);

      Sentry.captureMessage(`More than ${SLICES_LIMIT} slices exist for creator '${context.creatorName}'`, 'warning');
    });
  }

  private buildSlicesParams(
    creatorId: string,
    campaignIds: string[],
    filter: ICreatorDetailsDeliverablesFilter,
  ): HttpParams {
    let params = new HttpParams()
      .set('creator_id', creatorId)
      .set('campaign_id__in', campaignIds.join(','))
      .set('joins', 'social_contents.social_content')
      .set('limit', String(SLICES_LIMIT));

    if (filter.startDate) {
      params = params.set('start__gte', format(filter.startDate, API_DATE_TIME_FORMAT_WITH_MS)!);
    }

    if (filter.endDate) {
      params = params.set('end__lte', format(filter.endDate, API_DATE_TIME_FORMAT_WITH_MS)!);
    }

    if (filter.platformFeatures.size > 0) {
      params = params.set(
        'platform_feature__in',
        Array.from(filter.platformFeatures)
          .map((platformFeature) => upperFirst(platformFeature))
          .join(','),
      );
    }

    return params;
  }

  private groupSlicesByCampaignId(slices: ICreatorAnalyticsSliceApi[]): Map<string, ICreatorAnalyticsSliceApi[]> {
    const slicesByCampaignId = new Map<string, ICreatorAnalyticsSliceApi[]>();

    slices.forEach((slice) => {
      const existing = slicesByCampaignId.get(slice.campaignId) ?? [];
      existing.push(slice);
      slicesByCampaignId.set(slice.campaignId, existing);
    });

    return slicesByCampaignId;
  }

  private withSyntheticCampaignCreator(campaign: TCampaignApi, creator: ICreator): TCampaignApi {
    return {
      ...campaign,
      campaignCreators: [{ campaignId: campaign.id, creator, creatorId: creator.id } as ICampaignCreator],
    };
  }

  private buildCampaignShell(
    campaign: TCampaignApi,
    baseApi: ICampaignAnalyticsApi,
    shellApi: ICreatorAnalyticsApi | undefined,
  ): ICampaignAnalytics {
    const patchedApi: ICampaignAnalyticsApi = {
      ...baseApi,
      creatorAnalytics: shellApi ? [shellApi] : [],
    };
    const [shell] = this.campaignAnalyticsUtilities.getCampaignAnalyticsData(campaign, patchedApi);

    return shell;
  }

  // Only a shell with a real entry for THIS creator is usable as the merge base/template (see
  // buildScopeCampaignAnalytics below) - a campaign whose CreatorAnalytics doc hasn't been
  // created yet (creatorEntry undefined) still contributed its own deliverables-table nodes
  // (handled by the caller before this runs), it just can't seed the merged scope shell.
  private mergeIntoScopeState(campaign: TCampaignApi, shell: ICampaignAnalytics, mergeState: IScopeMergeState): void {
    const [creatorEntry] = Array.from(shell.creators.values());

    if (!creatorEntry) {
      return;
    }

    if (!mergeState.templateShell) {
      mergeState.templateShell = shell;
    }

    if (!mergeState.minStart || shell.start.getTime() < mergeState.minStart.getTime()) {
      mergeState.minStart = shell.start;
    }

    if (!mergeState.maxEnd || shell.end.getTime() > mergeState.maxEnd.getTime()) {
      mergeState.maxEnd = shell.end;
    }

    mergeState.creatorId = creatorEntry.id;
    creatorEntry.plannedCtas.forEach((plannedCta, ctaId) => {
      // Namespaced by campaignId so two campaigns' CTAs never collide in the merged map, even if
      // a ctaId were ever reused across campaigns (see the same defensive rationale in
      // campaign-details-deliverables.service.ts).
      mergeState.mergedPlannedCtas.set(`${campaign.id}::${ctaId}`, plannedCta);
    });
  }

  private buildScopeCampaignAnalytics(mergeState: IScopeMergeState): ICampaignAnalytics | null {
    const { creatorId, maxEnd, mergedPlannedCtas, minStart, templateShell } = mergeState;

    if (!templateShell || !creatorId || !minStart || !maxEnd) {
      return null;
    }

    const [templateCreator] = Array.from(templateShell.creators.values());
    const mergedCreator: ICreatorAnalytics = {
      ...templateCreator,
      id: creatorId,
      plannedCtas: mergedPlannedCtas,
    };

    return {
      ...templateShell,
      creators: new Map([[creatorId, mergedCreator]]),
      end: maxEnd,
      start: minStart,
    };
  }
}
